import {
  InvoiceCreateDTO,
  InvoiceGeneralFormValues,
} from '@/core/domain/invoice/invoice/Invoice.types';
import {
  InvoiceEditNFc,
  InvoiceEditNFe,
  InvoiceEditNFs,
} from '@/core/domain/invoice/invoices/invoices.editTypes';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { DateTimeFormat } from '@/utils/moment';
import { useCallback } from 'react';

function parseBoolean(formValue?: string, invoiceValue?: boolean): boolean {
  return formValue !== undefined ? formValue === 'true' : !!invoiceValue;
}

function parseNumber(value?: string | boolean | null): number | undefined {
  return value != null && value !== '' ? Number(value) : undefined;
}

export function getInvoiceDataToUpdate(invoice?: InvoiceCreateDTO) {
  const getNfeDataUpdate = useCallback(
    (
      formValues: Partial<InvoiceGeneralFormValues>,
    ): InvoiceEditNFe | undefined => {
      if (!invoice) return;

      return {
        invoiceType: InvoiceType.NFE,
        ...invoice,
        ...formValues,
        ...formValues.recipient,
        faceToFace: parseBoolean(formValues.faceToFace, invoice.faceToFace),
        finalConsumer: parseBoolean(
          formValues.finalConsumer,
          invoice.finalConsumer,
        ),
        simpleNational: parseBoolean(
          formValues.simpleNational,
          invoice.simpleNational === 'true',
        ),
        departureDate: formValues.departureDate
          ? formValues.departureDate.format(DateTimeFormat)
          : invoice.departureDate,
        emissionDate: formValues.emissionDate
          ? formValues.emissionDate.format(DateTimeFormat)
          : invoice.emissionDate,
        intermediaryIndication: formValues.intermediaryIndication
          ? Number(JSON.parse(formValues.intermediaryIndication))
          : invoice.intermediaryIndication
          ? Number(JSON.parse(String(invoice.intermediaryIndication)))
          : undefined,
        natureOfOperation: formValues.natureOfOperation
          ? String(formValues.natureOfOperation)
          : invoice.natureOfOperation,
        // Tax reform ------------
        deliveryPrevisionDate:
          formValues.deliveryPrevisionDate?.format('YYYY-MM-DD'),
        occurrenceCityIbsCbs: formValues.occurrenceCityIbsCbs,
        governmentType: formValues.governmentType,
        governmentReducer: formValues.governmentReducer,
        governmentOperation: formValues.governmentOperation,
      };
    },
    [invoice],
  );

  const getNfcDataUpdate = useCallback(
    (
      formValues: Partial<InvoiceGeneralFormValues>,
    ): InvoiceEditNFc | undefined => {
      if (!invoice) return;

      return {
        invoiceType: InvoiceType.NFC,
        ...invoice,
        ...formValues,
        faceToFace: parseBoolean(formValues.faceToFace, invoice.faceToFace),
        finalConsumer: parseBoolean(
          formValues.finalConsumer,
          invoice.finalConsumer,
        ),
        simpleNational: parseBoolean(
          formValues.simpleNational,
          invoice.simpleNational === 'true',
        ),
        emissionDate: formValues.emissionDate
          ? formValues.emissionDate.format(DateTimeFormat)
          : invoice.emissionDate,
        intermediaryIndication: formValues.intermediaryIndication
          ? Number(JSON.parse(formValues.intermediaryIndication))
          : invoice.intermediaryIndication
          ? Number(JSON.parse(String(invoice.intermediaryIndication)))
          : undefined,
        natureOfOperation: formValues.natureOfOperation
          ? String(formValues.natureOfOperation)
          : invoice.natureOfOperation,
        // Tax reform ------------
        deliveryPrevisionDate:
          formValues.deliveryPrevisionDate?.format('YYYY-MM-DD'),
        occurrenceCityIbsCbs: formValues.occurrenceCityIbsCbs,
        governmentType: formValues.governmentType,
        governmentReducer: formValues.governmentReducer,
        governmentOperation: formValues.governmentOperation,
      };
    },
    [invoice],
  );

  const getNfsDataUpdate = useCallback(
    (
      formValues: Partial<InvoiceGeneralFormValues>,
    ): InvoiceEditNFs | undefined => {
      if (!invoice) return;

      return {
        invoiceType: InvoiceType.NFS,
        ...invoice,
        ...formValues,
        publicAgency: formValues.publicAgency
          ? parseBoolean(
              String(formValues.publicAgency),
              invoice.publicAgency === 'true',
            )
          : undefined,
        simpleNational: parseBoolean(
          formValues.simpleNational,
          invoice.simpleNational === 'true',
        ),
        culturalIncentivator: parseBoolean(
          formValues.culturalIncentivator,
          invoice.culturalIncentivator === 'true',
        ),
        intermediaryIndication: parseNumber(
          typeof formValues.intermediaryIndicationNfs === 'boolean'
            ? formValues.intermediaryIndicationNfs
            : invoice.intermediaryIndication,
        ),
        // Tax reform ------------
        deliveryPrevisionDate:
          formValues.deliveryPrevisionDate?.format('YYYY-MM-DD'),
        occurrenceCityIbsCbs: formValues.occurrenceCityIbsCbs,
        governmentType: formValues.governmentType,
        governmentReducer: formValues.governmentReducer,
        governmentOperation: formValues.governmentOperation,
      };
    },
    [invoice],
  );

  return { getNfeDataUpdate, getNfcDataUpdate, getNfsDataUpdate };
}
